import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  PayloadAction,
  EntityState,
} from "@reduxjs/toolkit";
import type { RootState } from "@/app/types";
import * as badgeAwardApi from "@/api/badgeAwards";
import type {
  BadgeAward,
  BadgeAwardFilters,
  ManualAwardRequest,
  UserBadgeProgress,
} from "../types/badgeAward.types";

/**
 * Badge Award Slice
 * Manages badge award tracking and user progress
 * Part of Feature: Badge and Points Management System
 */

// Entity adapter for normalized state management
const badgeAwardsAdapter = createEntityAdapter<BadgeAward, string>({
  selectId: (award: BadgeAward) => award.id,
  sortComparer: (a, b) => {
    // Sort by awardedAt descending (most recent first)
    return new Date(b.awardedAt).getTime() - new Date(a.awardedAt).getTime();
  },
});

// State interface
interface BadgeAwardState {
  awards: EntityState<BadgeAward, string>;
  userProgress: Record<string, UserBadgeProgress>; // userId -> progress
  realtimeAwards: BadgeAward[]; // Recent real-time awards for notifications
  loading: boolean;
  error: string | null;
  manualAwardLoading: boolean;
  manualAwardError: string | null;
  filters: {
    userId?: string;
    badgeId?: string;
    enrollmentId?: string;
    startDate?: string;
    endDate?: string;
  };
  total: number;
}

// Initial state
const initialState: BadgeAwardState = {
  awards: badgeAwardsAdapter.getInitialState(),
  userProgress: {},
  realtimeAwards: [],
  loading: false,
  error: null,
  manualAwardLoading: false,
  manualAwardError: null,
  filters: {
    userId: undefined,
    badgeId: undefined,
    enrollmentId: undefined,
    startDate: undefined,
    endDate: undefined,
  },
  total: 0,
};

// Async Thunks

/**
 * Fetch badge awards with filters
 */
export const fetchBadgeAwards = createAsyncThunk(
  "badgeAwards/fetchBadgeAwards",
  async (filters: BadgeAwardFilters | undefined, { rejectWithValue }) => {
    try {
      const awards = await badgeAwardApi.fetchBadgeAwards(filters);
      return awards;
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Failed to fetch badge awards",
      );
    }
  },
);

/**
 * Fetch badge awards for a specific user
 */
export const fetchUserBadgeAwards = createAsyncThunk(
  "badgeAwards/fetchUserBadgeAwards",
  async (userId: string, { rejectWithValue }) => {
    try {
      const awards = await badgeAwardApi.fetchBadgeAwards({ userId });
      return { userId, awards };
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Failed to fetch user badge awards",
      );
    }
  },
);

/**
 * Manually award a badge to a user
 */
export const manuallyAwardBadge = createAsyncThunk(
  "badgeAwards/manuallyAwardBadge",
  async (payload: ManualAwardRequest, { rejectWithValue }) => {
    try {
      const award = await badgeAwardApi.manuallyAwardBadge(payload);
      return award;
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Failed to award badge",
      );
    }
  },
);

/**
 * Fetch user badge progress (aggregated stats)
 */
export const fetchUserBadgeProgress = createAsyncThunk(
  "badgeAwards/fetchUserBadgeProgress",
  async (userId: string, { rejectWithValue }) => {
    try {
      const progress = await badgeAwardApi.fetchUserBadgeProgress(userId);
      return { userId, progress };
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Failed to fetch user badge progress",
      );
    }
  },
);

// Slice
const badgeAwardSlice = createSlice({
  name: "badgeAwards",
  initialState,
  reducers: {
    /**
     * Add a real-time badge award (from GraphQL subscription)
     */
    addRealtimeAward: (state, action: PayloadAction<BadgeAward>) => {
      badgeAwardsAdapter.addOne(state.awards, action.payload);
      state.total += 1;

      // Add to realtime awards for notifications (keep last 10)
      state.realtimeAwards.unshift(action.payload);
      if (state.realtimeAwards.length > 10) {
        state.realtimeAwards.pop();
      }

      // Update user progress if exists
      const userId = action.payload.userId;
      if (state.userProgress[userId]) {
        state.userProgress[userId].totalPoints += action.payload.pointsAwarded;
        state.userProgress[userId].totalBadges += 1;
      }
    },

    /**
     * Clear real-time awards (dismiss notifications)
     */
    clearRealtimeAwards: (state) => {
      state.realtimeAwards = [];
    },

    /**
     * Dismiss a single real-time award notification
     */
    dismissRealtimeAward: (state, action: PayloadAction<string>) => {
      state.realtimeAwards = state.realtimeAwards.filter(
        (award) => award.id !== action.payload,
      );
    },

    /**
     * Set filters
     */
    setFilters: (
      state,
      action: PayloadAction<Partial<BadgeAwardState["filters"]>>,
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    /**
     * Reset filters
     */
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    /**
     * Clear errors
     */
    clearErrors: (state) => {
      state.error = null;
      state.manualAwardError = null;
    },

    /**
     * Clear all awards
     */
    clearAwards: (state) => {
      state.awards = badgeAwardsAdapter.getInitialState();
      state.total = 0;
    },

    /**
     * Clear user progress
     */
    clearUserProgress: (state) => {
      state.userProgress = {};
    },
  },
  extraReducers: (builder) => {
    // Fetch badge awards
    builder
      .addCase(fetchBadgeAwards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBadgeAwards.fulfilled, (state, action) => {
        state.loading = false;
        badgeAwardsAdapter.setAll(state.awards, action.payload);
        state.total = action.payload.length;
      })
      .addCase(fetchBadgeAwards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch user badge awards
    builder
      .addCase(fetchUserBadgeAwards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserBadgeAwards.fulfilled, (state, action) => {
        state.loading = false;
        const { userId, awards } = action.payload;

        // Add awards to state
        badgeAwardsAdapter.upsertMany(state.awards, awards);

        // Calculate user progress
        const totalPoints = awards.reduce(
          (sum, award) => sum + award.pointsAwarded,
          0,
        );
        const uniqueBadges = new Set(awards.map((award) => award.badgeId));

        state.userProgress[userId] = {
          userId,
          totalPoints,
          totalBadges: uniqueBadges.size,
          recentBadges: awards.slice(0, 5),
          pathProgress: [],
        };
      })
      .addCase(fetchUserBadgeAwards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Manually award badge
    builder
      .addCase(manuallyAwardBadge.pending, (state) => {
        state.manualAwardLoading = true;
        state.manualAwardError = null;
      })
      .addCase(manuallyAwardBadge.fulfilled, (state, action) => {
        state.manualAwardLoading = false;
        badgeAwardsAdapter.addOne(state.awards, action.payload);
        state.total += 1;

        // Update user progress if exists
        const userId = action.payload.userId;
        if (state.userProgress[userId]) {
          state.userProgress[userId].totalPoints +=
            action.payload.pointsAwarded;
          state.userProgress[userId].totalBadges += 1;
          state.userProgress[userId].recentBadges.unshift(action.payload);
          // Keep only last 5 recent awards
          if (state.userProgress[userId].recentBadges.length > 5) {
            state.userProgress[userId].recentBadges.pop();
          }
        }
      })
      .addCase(manuallyAwardBadge.rejected, (state, action) => {
        state.manualAwardLoading = false;
        state.manualAwardError = action.payload as string;
      });

    // Fetch user badge progress
    builder
      .addCase(fetchUserBadgeProgress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserBadgeProgress.fulfilled, (state, action) => {
        state.loading = false;
        const { userId, progress } = action.payload;
        state.userProgress[userId] = progress;
      })
      .addCase(fetchUserBadgeProgress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Actions
export const {
  addRealtimeAward,
  clearRealtimeAwards,
  dismissRealtimeAward,
  setFilters,
  resetFilters,
  clearErrors,
  clearAwards,
  clearUserProgress,
} = badgeAwardSlice.actions;

// Selectors using entity adapter
export const {
  selectAll: selectAllAwards,
  selectById: selectAwardById,
  selectIds: selectAwardIds,
  selectEntities: selectAwardEntities,
  selectTotal: selectAwardsCount,
} = badgeAwardsAdapter.getSelectors(
  (state: RootState) => state.badgeAwards.awards,
);

// Custom selectors
export const selectFilteredAwards = (state: RootState) => {
  const awards = selectAllAwards(state);
  const filters = state.badgeAwards.filters;

  return awards.filter((award) => {
    // Filter by user ID
    if (filters.userId && award.userId !== filters.userId) {
      return false;
    }

    // Filter by badge ID
    if (filters.badgeId && award.badgeId !== filters.badgeId) {
      return false;
    }

    // Filter by enrollment ID
    if (filters.enrollmentId && award.enrollmentId !== filters.enrollmentId) {
      return false;
    }

    // Filter by date range
    if (filters.startDate) {
      const awardDate = new Date(award.awardedAt);
      const startDate = new Date(filters.startDate);
      if (awardDate < startDate) {
        return false;
      }
    }

    if (filters.endDate) {
      const awardDate = new Date(award.awardedAt);
      const endDate = new Date(filters.endDate);
      if (awardDate > endDate) {
        return false;
      }
    }

    return true;
  });
};

export const selectUserAwards = (userId: string) => (state: RootState) => {
  return selectAllAwards(state).filter((award) => award.userId === userId);
};

export const selectBadgeAwards = (badgeId: string) => (state: RootState) => {
  return selectAllAwards(state).filter((award) => award.badgeId === badgeId);
};

export const selectRecentAwards =
  (limit = 10) =>
  (state: RootState) => {
    return selectAllAwards(state).slice(0, limit);
  };

export const selectRealtimeAwards = (state: RootState) =>
  state.badgeAwards.realtimeAwards;

export const selectUserProgress = (userId: string) => (state: RootState) => {
  return state.badgeAwards.userProgress[userId] || null;
};

export const selectAllUserProgress = (state: RootState) =>
  state.badgeAwards.userProgress;

export const selectAwardsLoading = (state: RootState) =>
  state.badgeAwards.loading;

export const selectAwardsError = (state: RootState) => state.badgeAwards.error;

export const selectManualAwardLoading = (state: RootState) =>
  state.badgeAwards.manualAwardLoading;

export const selectManualAwardError = (state: RootState) =>
  state.badgeAwards.manualAwardError;

export const selectAwardsFilters = (state: RootState) =>
  state.badgeAwards.filters;

export const selectAwardsTotal = (state: RootState) => state.badgeAwards.total;

// Aggregate selectors
export const selectTotalPointsByUser =
  (userId: string) => (state: RootState) => {
    const progress = state.badgeAwards.userProgress[userId];
    return progress?.totalPoints || 0;
  };

export const selectTotalBadgesByUser =
  (userId: string) => (state: RootState) => {
    const progress = state.badgeAwards.userProgress[userId];
    return progress?.totalBadges || 0;
  };

// Reducer
export default badgeAwardSlice.reducer;
